/**
 * @file        src/components/ThemeToggle.jsx
 * @description Light/Dark theme switcher shown in the sidebar chrome (visible on
 *              every screen). A premium segmented pill (Sun / Moon) in the expanded
 *              sidebar and an icon-only button when the sidebar is collapsed or on
 *              mobile. Drives useTheme — instant, no reload, persisted. Purely a
 *              presentational control; touches no API/route/auth/business logic.
 * @author      Kinetika Labs
 * @created     2026-07-15
 * @changelog
 *   - 2026-08-04 Claude — `sky` knob now FILLS the track: padding dropped (p-0.5/p-1)
 *                          and the knob grown to the track's inner size, 20→22px (sm)
 *                          and 28→34px (md). It used to be 2px taller than the padded
 *                          space with nothing centring it, so it hung off the bottom
 *                          border while a crescent of bare track showed around its top
 *                          and left — the visible gap, in both themes. Travel stays
 *                          24/40 and is now exact. The light-mode ring went with it: a
 *                          ring paints outside the box and would smear over the
 *                          outline, and a flush knob no longer needs framing.
 *   - 2026-08-03 Claude — Light-mode outline is solid Jet Ink #1C1C1C (client request),
 *                          replacing the mid-grey it shipped with hours earlier.
 *   - 2026-08-03 Claude — `sky` track is now TRANSPARENT in light mode (outline only);
 *                          dark keeps the solid Jet Ink fill. The knob gained a
 *                          light-mode ring so a white circle can't vanish on a pale
 *                          surface once the track stops framing it.
 *   - 2026-08-03 Claude — `sky` variant restyled to the client's reference icon: a flat
 *                          Jet Ink track with a white knob carrying the state glyph
 *                          (sun light / crescent moon dark). The skeuomorphic
 *                          sun-gradient, clouds, stars and moon-crater decorations are
 *                          gone; track/knob/travel sizes are UNCHANGED, so nothing in
 *                          the top nav or sidebar shifts. Mirrored in booking_frontend.
 *   - 2026-07-29 Claude — Brand sweep: the teal→green CTA gradient / solid teal
 *                          button + active-tab fills → brand black (#1C1C1C) with
 *                          white text. Teal is kept for non-button accents (dots,
 *                          ticks, badges, step circles, calendar cells).
 *   - 2026-07-20 Claude — Added the `row` layout to the sky variant: label left,
 *                          pill right, full width — a labelled settings row for the
 *                          sidebar footer. The label stays inside this component
 *                          because useTheme is per-component state with no shared
 *                          store, so a parent-rendered label would go stale on
 *                          toggle. Default (stacked) layout is unchanged.
 *   - 2026-07-16 Claude — Both tracks now use our theme: dark = navy (from-night
 *                          to-panel, border-line); light = brand green (from-mint
 *                          to-neon, border-neon/40) instead of slate/indigo & sky-blue.
 *   - 2026-07-16 Claude — Removed the native `title` tooltip on the sky toggle
 *                          (the "Light mode"/"Dark mode" hover popup). aria-label
 *                          kept for screen readers.
 *   - 2026-07-16 Claude — Smoothed the sky knob glide: replaced the bouncy
 *                          overshoot spring (cubic-bezier .34,1.56 @500ms) with a
 *                          clean ease-out (.22,.61,.36,1 @300ms) so the slide reads
 *                          smooth, not janky.
 *   - 2026-07-16 Claude — Added the `sky` variant: a skeuomorphic day/night pill
 *                          (sliding sun + clouds ⟷ moon + stars) with a "Light mode"
 *                          / "Dark mode" label. `showLabel` toggles the caption.
 *   - 2026-07-15 Claude — Initial.
 */
import React from 'react';
import useTheme from '../hooks/useTheme.js';

const SunIcon = ({ className = 'w-4 h-4' }) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
    <path fill="currentColor" d="M12 17a5 5 0 1 1 0-10 5 5 0 0 1 0 10m0-2a3 3 0 1 0 0-6 3 3 0 0 0 0 6m0-13a1 1 0 0 1 1 1v2a1 1 0 1 1-2 0V3a1 1 0 0 1 1-1m0 16a1 1 0 0 1 1 1v2a1 1 0 1 1-2 0v-2a1 1 0 0 1 1-1M4 11a1 1 0 1 1 0 2H2a1 1 0 1 1 0-2zm18 0a1 1 0 1 1 0 2h-2a1 1 0 1 1 0-2zM5.6 4.2l1.4 1.4A1 1 0 1 1 5.6 7L4.2 5.6a1 1 0 0 1 1.4-1.4m12.8 12.8 1.4 1.4a1 1 0 0 1-1.4 1.4L17 18.4a1 1 0 0 1 1.4-1.4M4.2 18.4 5.6 17A1 1 0 0 1 7 18.4l-1.4 1.4a1 1 0 0 1-1.4-1.4M17 5.6l1.4-1.4a1 1 0 0 1 1.4 1.4L18.4 7A1 1 0 0 1 17 5.6" />
  </svg>
);
const MoonIcon = ({ className = 'w-4 h-4' }) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
    <path fill="currentColor" d="M12.3 3a1 1 0 0 1 .1 1.8 6.5 6.5 0 0 0 6.8 11 1 1 0 0 1 1.3 1.4A8.5 8.5 0 1 1 11.4 3.1a1 1 0 0 1 .9-.1" />
  </svg>
);

export default function ThemeToggle({ mini = false, variant = 'segment', size = 'md', className = '', showLabel = true, row = false }) {
  const { isDark, toggle, setTheme } = useTheme();

  // Flat day/night pill matching the client's reference icon: a white knob glides
  // along a solid Jet Ink track, carrying a sun in light mode and a crescent moon in
  // dark. Purely presentational — same useTheme drive as the other variants. Only the
  // track/knob dimensions, knob travel and glyph size change between sizes.
  // (Was a skeuomorphic sky with gradient sun, clouds, stars and moon craters.)
  if (variant === 'sky') {
    const sm = size === 'sm';
    // NO PADDING, and the knob FILLS the track. The pill used to carry p-0.5/p-1
    // with a knob 2px taller than the space that left, so the ball hung off the
    // bottom border while a crescent of empty track showed around its top and left
    // — the visible "gap". Padding is gone instead of the ball being shrunk: the
    // knob is now exactly the track's inner height (height − 2×border), so it seals
    // the rounded end it rests against.
    const track = sm ? 'w-[48px] h-6' : 'w-[76px] h-9';
    // Inner height: 24−2 = 22 (sm), 36−2 = 34 (md). Both are LARGER than the old
    // 20/28 knob, so the ball grows rather than shrinks.
    const knob = sm ? 'h-[22px] w-[22px]' : 'h-[34px] w-[34px]';
    // Travel = inner WIDTH − knob: (48−2)−22 = 24 and (76−2)−34 = 40 — the same two
    // numbers as before, now exact at both ends instead of overshooting the padding.
    const onX = sm ? 'translate-x-[24px]' : 'translate-x-[40px]';
    const glyph = sm ? 'w-3 h-3' : 'w-4 h-4';
    // `row`: label on the LEFT, pill on the right, filling the width — so the
    // control reads as a labelled settings row (the shape the sidebar's Wallet
    // row directly above it already uses). Default stays the stacked pill.
    // (The label had to live here while useTheme held per-component state — a
    // parent-rendered one went stale the moment this button flipped the theme.
    // useTheme is a shared store as of 2026-07-20, so that constraint is gone;
    // kept here as the natural home for it.)
    const label = showLabel ? (
      <span className={(row ? 'text-sm font-medium text-muted' : (sm ? 'text-xs' : 'text-sm') + ' font-semibold text-ink')}>
        {isDark ? 'Dark mode' : 'Light mode'}
      </span>
    ) : null;
    return (
      <div className={(row ? 'flex w-full items-center justify-between gap-3 ' : 'inline-flex flex-col gap-1.5 ') + className}>
        {row && label}
        <button
          type="button"
          role="switch"
          aria-checked={isDark}
          onClick={toggle}
          aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
          className={
            // Dark: the flat Jet Ink track from the client's reference icon.
            // Light: the fill drops out and the pill becomes an outline — a SOLID
            // Jet Ink hairline (per client request), the same black as the dark
            // state's fill, so the outline reads as the same object unfilled.
            'relative rounded-full cursor-pointer border transition-colors duration-200 '
            + track + ' '
            + (isDark ? 'border-white/15 bg-[#1C1C1C]' : 'border-[#1C1C1C] bg-transparent')
          }
        >
          {/* Sliding white knob carrying the state glyph: sun when light, crescent
              moon when dark. It is exactly the track's inner size, so it meets the
              border on three sides and seals the rounded end it rests against.
              The old light-mode `ring-1 ring-ink/20` is gone: a Tailwind ring paints
              OUTSIDE the box, so against a flush knob it would smear grey over the
              Jet Ink outline. It existed to keep a white knob visible on a pale
              backdrop "now that the track no longer frames it" — the flush knob is
              framed by that outline again, so the ring has nothing left to do. */}
          <span
            className={
              'relative z-10 flex items-center justify-center rounded-full bg-white text-[#1C1C1C] shadow-sm transition-transform duration-200 '
              + knob + ' [transition-timing-function:cubic-bezier(.22,.61,.36,1)] '
              + (isDark ? onX : 'translate-x-0')
            }
          >
            {isDark ? <MoonIcon className={glyph} /> : <SunIcon className={glyph} />}
          </span>
        </button>
        {!row && label}
      </div>
    );
  }

  if (mini) {
    return (
      <button
        type="button"
        onClick={toggle}
        aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
        title={isDark ? 'Light mode' : 'Dark mode'}
        className={
          'w-11 h-11 mx-auto rounded-xl border border-line bg-night text-muted flex items-center justify-center '
          + 'cursor-pointer hover:text-neon hover:border-neon transition-colors ' + className
        }
      >
        {isDark ? <SunIcon className="w-5 h-5" /> : <MoonIcon className="w-5 h-5" />}
      </button>
    );
  }

  return (
    <div
      role="group"
      aria-label="Theme"
      className={'flex items-center gap-1 p-1 rounded-xl border border-line bg-night/60 ' + className}
    >
      <button
        type="button"
        onClick={() => setTheme('light')}
        aria-pressed={!isDark}
        className={
          'flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold cursor-pointer transition-colors '
          + (!isDark ? 'bg-[#1C1C1C] text-white' : 'text-muted hover:text-ink')
        }
      >
        <SunIcon className="w-3.5 h-3.5" /> Light
      </button>
      <button
        type="button"
        onClick={() => setTheme('dark')}
        aria-pressed={isDark}
        className={
          'flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold cursor-pointer transition-colors '
          + (isDark ? 'bg-[#1C1C1C] text-white' : 'text-muted hover:text-ink')
        }
      >
        <MoonIcon className="w-3.5 h-3.5" /> Dark
      </button>
    </div>
  );
}
